/**
 * Data layer for `api-tracking`. Thin typed fetch wrappers over /api/v1/api-tracking.
 * Mirrors the BE surface at apps/api/src/routes/api-tracking.ts:
 *   POST /click, POST /convert, GET /click/:id, GET /convert/:id.
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 */

export interface ApiTrackingClickBody {
	campaign_id: string;
	affiliate_id?: string | null;
	sub1?: string | null;
	sub2?: string | null;
	sub3?: string | null;
	sub4?: string | null;
	sub5?: string | null;
	ip?: string | null;
	ua?: string | null;
	external_id?: string | null;
}

export interface ApiTrackingClickResponse {
	click_id: string;
	ts: number;
}

export interface ApiTrackingConvertBody {
	click_id: string;
	goal_id?: string | null;
	payout_cents?: number;
	revenue_cents?: number;
	external_id?: string | null;
	meta?: string | Record<string, unknown> | null;
}

export interface ApiTrackingConvertResponse {
	conversion_id: string;
	click_id: string;
	ts: number;
}

export interface ApiTrackingClickRow {
	id: string;
	tenant_id: string;
	campaign_id: string;
	affiliate_id: string | null;
	sub1: string | null;
	sub2: string | null;
	sub3: string | null;
	sub4: string | null;
	sub5: string | null;
	ip: string | null;
	ua: string | null;
	external_id: string | null;
	ts: number;
}

export interface ApiTrackingConversionRow {
	id: string;
	tenant_id: string;
	click_id: string;
	goal_id: string | null;
	payout_cents: number;
	revenue_cents: number;
	external_id: string | null;
	meta: string | null;
	ts: number;
}

const BASE = "/api/v1/api-tracking";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function postClick(
	body: ApiTrackingClickBody,
): Promise<ApiTrackingClickResponse> {
	const res = await fetch(`${BASE}/click`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<ApiTrackingClickResponse>(res);
}

export async function postConvert(
	body: ApiTrackingConvertBody,
): Promise<ApiTrackingConvertResponse> {
	const res = await fetch(`${BASE}/convert`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<ApiTrackingConvertResponse>(res);
}

export async function getClick(
	clickId: string,
): Promise<{ click: ApiTrackingClickRow }> {
	const res = await fetch(
		`${BASE}/click/${encodeURIComponent(clickId)}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<{ click: ApiTrackingClickRow }>(res);
}

export async function getConversion(
	conversionId: string,
): Promise<{ conversion: ApiTrackingConversionRow }> {
	const res = await fetch(
		`${BASE}/convert/${encodeURIComponent(conversionId)}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<{ conversion: ApiTrackingConversionRow }>(res);
}

export const apiTrackingQueryKeys = {
	all: ["api-tracking"] as const,
	click: (id: string) => [...apiTrackingQueryKeys.all, "click", id] as const,
	conversion: (id: string) =>
		[...apiTrackingQueryKeys.all, "conversion", id] as const,
};
